/**
 * Data-quality banner (epic-6).
 *
 * Surfaces the funnel read-model's eventual-consistency / degradation signals so
 * the partner is never shown stale or zeroed numbers as if they were live:
 *   - degraded -> "metrics updating…" (prizes unavailable; last-known/zeroed)
 *   - stale    -> "data may be delayed" with the as-of time
 * Both are non-blocking, polite (role="status") hints — the dashboard still
 * renders the best available numbers underneath.
 */
import { Icon } from '../../components/Icon';
import { formatAsOf } from './funnelLabels';

interface Props {
  degraded: boolean;
  stale: boolean;
  asOf: string | null;
}

export function DataQualityBanner({ degraded, stale, asOf }: Props) {
  if (!degraded && !stale) return null;

  const asOfLabel = formatAsOf(asOf);

  // Degraded is the stronger signal; show it alone if present.
  if (degraded) {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-xl border border-warm-400/40 bg-warm-400/10 px-4 py-3 text-sm text-ink-700"
      >
        <Icon name="clock" className="mt-0.5 h-5 w-5 shrink-0 text-warm-600" />
        <p>
          <span className="font-semibold text-ink">Metrics updating…</span>{' '}
          Live data is briefly unavailable, so figures below show the most recent
          known values. They will refresh automatically.
        </p>
      </div>
    );
  }

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-line bg-surface-alt px-4 py-3 text-sm text-ink-700"
    >
      <Icon name="clock" className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
      <p>
        <span className="font-semibold text-ink">Data may be delayed.</span>{' '}
        These figures are accurate as of{' '}
        {asOfLabel ? <time dateTime={asOf ?? undefined}>{asOfLabel}</time> : 'a recent update'}
        {' '}and update on a short delay.
      </p>
    </div>
  );
}

export default DataQualityBanner;
